import { type CompositionV3Ent, EntKind } from '@revelio/data-access';

import type { SelectionListKey } from '../../../../../shared/selection-lists/types';
import {
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type PrimaryFilterId,
  type PrimarySelection,
} from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import { getEntKindForGeoOrRicsLevelId } from '../../../filters/utils/role-skill-ent-kind';
import type {
  DiversityCatalogEntity,
  DiversityCatalogRole,
} from '../../DataProvider/catalog/diversity-catalog';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../../DataProvider/types';
import type { PrimaryEntity } from '../../PageConfigProvider/types/chart';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { PlotOptions } from '../../PageConfigProvider/types/item';
import type { SubfilterTree } from '../../PageState/types';
import { isOtherSegment } from '../utils/group-and-filter-data';

const SUBFILTER_DIMENSION_TO_FILTER_ID: Partial<
  Record<CompositionSecondaryDimensionValues, FilterId>
> = {
  gender: FilterId.GENDER,
  ethnicity: FilterId.ETHNICITY,
  education: FilterId.EDUCATION,
  seniority: FilterId.SENIORITY,
};

const SUBFILTER_DIMENSION_TO_SELECTION_LIST_ID: Partial<
  Record<CompositionSecondaryDimensionValues, SelectionListKey>
> = {
  gender: 'gender',
  ethnicity: 'ethnicity',
  education: 'highest_degree',
  seniority: 'seniority',
};

/**
 * A combo that plots one focus company (see getFocusSelection) against its
 * competitors or industry.
 */
export const isDynamicEntityComparison = (
  plot: Pick<PlotOptions, 'divergingBarCombo'> | undefined,
  primaryDimension: PrimaryFilterId | undefined
): boolean =>
  plot?.divergingBarCombo?.dynamicEntityComparison === true &&
  primaryDimension === FilterId.COMPANY;

export const isSubfilterableDimension = (
  dimension: CompositionSecondaryDimensionValues | undefined
): boolean =>
  dimension !== undefined && dimension in SUBFILTER_DIMENSION_TO_FILTER_ID;

const getLeafId = (id: string | number | undefined | null): string | null => {
  if (id === undefined || id === null) return null;
  return String(id).split('_').slice(-1)[0] ?? null;
};

export const buildSubfilterNarrowingFilters = (
  dimension: CompositionSecondaryDimensionValues | undefined,
  selectedSegments: SubfilterTree[]
): Filter[] => {
  if (!dimension) return [];
  const filterId = SUBFILTER_DIMENSION_TO_FILTER_ID[dimension];
  const selectionListId = SUBFILTER_DIMENSION_TO_SELECTION_LIST_ID[dimension];
  if (!filterId || !selectionListId) return [];

  const selections = selectedSegments
    .map((segment) => {
      const leaf = getLeafId(segment?.id);
      return leaf ? { id: leaf, name: segment.name } : null;
    })
    .filter(
      (selection): selection is { id: string; name: string } =>
        selection !== null
    );

  if (selections.length === 0) return [];

  const filter: ListFilter = {
    id: filterId,
    type: FilterType.LIST,
    name: dimension,
    selectionListId,
    selections,
  };
  return [filter];
};

const buildCompanyEntity = (
  selection: PrimarySelection
): CompositionV3Ent | null => {
  const entType = getEntKindForGeoOrRicsLevelId(selection.levelId);
  const entId = getNumericIdFromNestedId(selection.id);
  if (!entType || entId === null) return null;
  return { EntType: entType, EntID: entId };
};

export const buildCompanyCompetitorEntities = (
  companySelection: PrimarySelection,
  competitorIds: number[]
): CompositionV3Ent[] | null => {
  const company = buildCompanyEntity(companySelection);
  if (!company) return null;

  const uniqueCompetitorIds = Array.from(new Set(competitorIds)).filter(
    (id) => Number.isFinite(id) && id > 0 && id !== company.EntID
  );

  return [
    company,
    ...uniqueCompetitorIds.map((EntID) => ({
      EntType: EntKind.Company,
      EntID,
    })),
  ];
};

export const buildCompanyIndustryEntities = (
  companySelection: PrimarySelection,
  ricsK400: number
): CompositionV3Ent[] | null => {
  const company = buildCompanyEntity(companySelection);
  if (!company) return null;

  return [company, { EntType: EntKind.RicsK400, EntID: ricsK400 }];
};

export type PenetrationPoint = SingleDataPoint & { denominator?: number };

export const buildPenetrationBreakdownSet = (
  numeratorSet: BreakdownSingleDataSet,
  denominatorSet: BreakdownSingleDataSet
): BreakdownSingleDataSet<PenetrationPoint> => {
  const data = denominatorSet.data.map((denominatorEntity, index) => {
    const numeratorEntity = numeratorSet.data[index];
    const numeratorByDim = new Map(
      (numeratorEntity?.breakdown ?? []).map((point) => [point.dim, point])
    );
    const breakdown: PenetrationPoint[] = denominatorEntity.breakdown.map(
      (denominatorPoint) => {
        const denominator =
          typeof denominatorPoint.value === 'number'
            ? denominatorPoint.value
            : 0;
        const numeratorPoint = numeratorByDim.get(denominatorPoint.dim);
        const numerator =
          typeof numeratorPoint?.value === 'number' ? numeratorPoint.value : 0;
        return {
          ...denominatorPoint,
          value: numerator,
          denominator,

          share: denominator > 0 ? numerator / denominator : null,
        };
      }
    );
    return { dim: denominatorEntity.dim, breakdown };
  });

  return { type: DataType.BREAKDOWN_SINGLE, data };
};

const CATALOG_ROLE_LIMIT = 7;

export type CatalogSegmentDimension = 'gender' | 'ethnicity';

export const isCatalogSegmentDimension = (
  dimension: CompositionSecondaryDimensionValues | undefined
): dimension is CatalogSegmentDimension =>
  dimension === 'gender' || dimension === 'ethnicity';

export const getSelectedSegmentIds = (
  selectedSegments: SubfilterTree[]
): number[] =>
  selectedSegments
    .map((segment) => Number(getLeafId(segment?.id)))
    .filter((id) => Number.isInteger(id) && id > 0);

type CatalogRoleShare = {
  name: string;
  numerator: number;
  denominator: number;
};

const catalogRoleShares = (
  rows: DiversityCatalogRole[],
  segmentIds: ReadonlySet<number>
): Map<number, CatalogRoleShare> => {
  const roles = new Map<number, CatalogRoleShare>();
  for (const row of rows) {
    const headcount = row.headcount[row.headcount.length - 1] ?? 0;
    const role = roles.get(row.id) ?? {
      name: row.name,
      numerator: 0,
      denominator: 0,
    };
    role.denominator += headcount;
    if (segmentIds.has(row.segmentId)) {
      role.numerator += headcount;
    }
    roles.set(row.id, role);
  }
  return roles;
};

export const buildCatalogPenetrationSet = ({
  entities,
  dimension,
  segmentIds,
}: {
  entities: DiversityCatalogEntity[];
  dimension: CatalogSegmentDimension;
  segmentIds: number[];
}): BreakdownSingleDataSet<PenetrationPoint> | null => {
  const selected = new Set(segmentIds);
  const shares = entities.map((entity) => ({
    entity,
    roles: catalogRoleShares(
      dimension === 'gender' ? entity.rolesByGender : entity.rolesByEthnicity,
      selected
    ),
  }));
  const focus = shares.find(({ entity }) => entity.index === 0);
  if (!focus) return null;

  const roleIds = [...focus.roles.entries()]
    .filter(([, role]) => role.numerator > 0)
    .sort(([, a], [, b]) => b.numerator - a.numerator)
    .slice(0, CATALOG_ROLE_LIMIT)
    .map(([id]) => id);

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: shares.map(({ entity, roles }) => ({
      dim: entity.name,
      breakdown: roleIds.flatMap((id): PenetrationPoint[] => {
        const role = roles.get(id);
        if (!role) return [];
        return [
          {
            dim: role.name,
            value: role.numerator,
            denominator: role.denominator,
            share:
              role.denominator > 0 ? role.numerator / role.denominator : null,
            drilldownId: String(id),
          },
        ];
      }),
    })),
  };
};

export const removeOtherSegments = (
  dataSet: BreakdownSingleDataSet
): BreakdownSingleDataSet => ({
  type: DataType.BREAKDOWN_SINGLE,
  data: dataSet.data.map((entity) => ({
    ...entity,
    breakdown: entity.breakdown.filter((point) => !isOtherSegment(point)),
  })),
});

export const aggregateCompetitorShares = (
  dataSet: BreakdownSingleDataSet,
  label: string
): BreakdownSingleDataSet => {
  const [focusEntity, ...competitorEntities] = dataSet.data;
  if (!focusEntity || competitorEntities.length === 0) return dataSet;

  const shareByDimension = new Map<
    string,
    { point: SingleDataPoint; sum: number; count: number }
  >();

  for (const entity of competitorEntities) {
    for (const point of entity.breakdown) {
      if (typeof point.share !== 'number' || Number.isNaN(point.share)) {
        continue;
      }

      const existing = shareByDimension.get(point.dim);
      if (existing) {
        existing.sum += point.share;
        existing.count += 1;
        continue;
      }

      shareByDimension.set(point.dim, {
        point,
        sum: point.share,
        count: 1,
      });
    }
  }

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: [
      focusEntity,
      {
        dim: label,
        breakdown: Array.from(shareByDimension.values()).map(
          ({ point, sum, count }) => ({
            ...point,
            value: null,
            share: sum / count,
          })
        ),
      },
    ],
  };
};

const matchesFocus = (
  selection: PrimarySelection,
  focus: PrimaryEntity | null | undefined
): boolean => {
  if (!focus) return false;
  return (
    getLeafId(selection.id) === getLeafId(focus.id) &&
    selection.levelId === focus.levelId
  );
};

export const getFocusSelection = (
  selections: PrimarySelection[],
  focus: PrimaryEntity | null | undefined
): PrimarySelection | null => {
  const index = Math.max(
    selections.findIndex((selection) => matchesFocus(selection, focus)),
    0
  );
  return selections[index] ?? null;
};

export const COMPETITOR_UNAVAILABLE_ERROR =
  'Competitor data is unavailable for this company.';
export const INDUSTRY_UNAVAILABLE_ERROR =
  'Industry data is unavailable for this company.';

type ComboEntityResolution =
  | { dataSet: BreakdownSingleDataSet; error?: undefined }
  | { dataSet?: undefined; error: string };

export const resolveComboEntities = ({
  converted,
  dynamic,
  selections,
  dynamicUnavailableError = COMPETITOR_UNAVAILABLE_ERROR,
}: {
  converted: BreakdownSingleDataSet;
  dynamic: boolean;
  selections: PrimarySelection[];
  dynamicUnavailableError?: string;
}): ComboEntityResolution => {
  const data = converted.data ?? [];

  if (dynamic && selections.length === 0) {
    return { error: 'Select at least one company to compare.' };
  }

  if (data.length !== 2) {
    return {
      error: dynamic
        ? dynamicUnavailableError
        : 'Diverging bar combo requires exactly two entities selected.',
    };
  }
  return { dataSet: converted };
};
